// ===========================================
// Lessons Page Helpers
// ===========================================

import { Lesson } from '../../../api/lessons.api';
import { GroupedLesson } from './lessons.types';
import { termWeekCount, lessonWindowWeeks } from '../../../utils/dateUtils';
import { classSessionWeeks } from '../../../utils/classWeeks';

// ===========================================
// TERM STATUS (past / current / future)
// ===========================================

export type TermStatus = 'past' | 'current' | 'future';

/**
 * UTC midnight of today, matching the backend `startOfTodayUTC` / `isTermEnded`. WARNING: this
 * answers "has the term FINISHED?" (a meaning question: default filters, badges) and is NEVER an
 * edit gate — a term is editable until the NEXT term starts. Edit gating is the `isLocked`
 * predicate from `useTermLock` (`utils/termLock.ts`, the school-day mirror of the backend lock).
 * A term ending TODAY is still current.
 */
function todayUTCms(): number {
  const n = new Date();
  return Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate());
}

/**
 * Classify a term as past / current / future from its ISO start/end dates (meaning only — see
 * `todayUTCms`; the edit gate is the term LOCK, not this).
 */
export function termStatus(term: { startDate: string; endDate: string }): TermStatus {
  const today = todayUTCms();
  const end = new Date(term.endDate).getTime();
  const start = new Date(term.startDate).getTime();
  if (end < today) return 'past';
  if (start > today) return 'future';
  return 'current';
}

/** The term has FINISHED. Not an edit gate: a finished term stays editable until the next one starts. */
export function isTermEnded(term: { startDate: string; endDate: string }): boolean {
  return termStatus(term) === 'past';
}

/**
 * The term to default a listing filter to: the CURRENT term (started, not yet ended). Derived from
 * dates via `termStatus` — `Term.isActive` is a soft-delete flag, not a "this is the live term"
 * marker, so it never decides which term is current (an active term can be long over). It does
 * rule a term OUT: a deactivated term is never the default, even when its dates overlap (#272).
 *
 * Falls back to the EARLIEST upcoming term when nothing is current (e.g. a between-terms break, so
 * the page opens on what's next rather than on nothing), and returns undefined when neither exists
 * — callers treat undefined as "no default", leaving the filter on All Terms.
 */
export function findCurrentTermId(
  allTerms: { id: string; startDate: string; endDate: string; isActive?: boolean }[]
): string | undefined {
  const terms = allTerms.filter((t) => t.isActive !== false);
  const current = terms.find((t) => termStatus(t) === 'current');
  if (current) return current.id;

  const nextUp = terms
    .filter((t) => termStatus(t) === 'future')
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())[0];

  return nextUp?.id;
}

/**
 * Which terms an edit touches. NOTE: `EditScopeModal` only offers 'this' and 'thisAndFuture' —
 * an "All terms" radio was removed as redundant (see that file's header). 'all' is still a valid
 * scope and is passed directly by LessonsPage for single-term edits and creates, which never open
 * the modal.
 */
export type EditScope = 'this' | 'thisAndFuture' | 'all';

/**
 * "Is this term LOCKED?" by id — `useTermLock().isLocked`. Unknown ids must answer false.
 * (A locked term is one whose successor has started on the school's day and has no live reopen.)
 */
export type TermLockedPredicate = (termId: string) => boolean;

/**
 * The term an edit of a (possibly multi-term) class should be ANCHORED to — i.e. the term the admin
 * is effectively acting on.
 *
 * A grouped class's `lesson.termId` is NOT reliable for this: `groupLessonsByProperties` spreads
 * whichever row the API returned first, and the backend `getLessons` orders only by dayOfWeek /
 * startTime — no term ordering at all. So on a class spanning Terms 1-3 the seed can name ENDED
 * Term 1 while the live terms are 2 and 3. Anything keyed off the raw seed then targets a term that
 * `filterTermsByScope` excludes from the update loop (silently writing nothing), scopes a partner
 * picker to a past term, or names the wrong term in the UI.
 *
 * Rule (the same anchor as `filterTermsByScope` while ANY spanned term is unlocked, so the two agree
 * wherever a write can happen): the viewed term when it is unlocked, else the EARLIEST UNLOCKED
 * spanned term. They differ only when EVERY spanned term is locked (nothing is written then): the
 * LATEST locked spanned term by start date — a stable, meaningful anchor for the partner
 * picker, window seeding and amend prompt rather than the API-order seed. Falls back to the raw
 * seed only when no spanned term is known — a stale/filtered `terms` list must not produce an
 * empty anchor.
 */
export function resolveAnchorTermId(
  lesson: { termId: string; termLessons?: { termId: string }[] },
  terms: { id: string; startDate: string; endDate: string }[],
  isLocked: TermLockedPredicate
): string {
  const spanned = lesson.termLessons?.length
    ? lesson.termLessons.map((tl) => tl.termId)
    : [lesson.termId];

  const viewed = terms.find((t) => t.id === lesson.termId);
  if (viewed && !isLocked(viewed.id) && spanned.includes(lesson.termId)) return lesson.termId;

  const known = spanned
    .map((id) => terms.find((t) => t.id === id))
    .filter((t): t is { id: string; startDate: string; endDate: string } => !!t);
  const byStart = (a: { startDate: string }, b: { startDate: string }) =>
    new Date(a.startDate).getTime() - new Date(b.startDate).getTime();

  const earliestUnlocked = known.filter((t) => !isLocked(t.id)).sort(byStart)[0];
  if (earliestUnlocked) return earliestUnlocked.id;

  const latestLocked = [...known].sort(byStart).reverse()[0];
  return latestLocked?.id ?? lesson.termId;
}

/**
 * Filter the set of existing term ids an edit should UPDATE, given the chosen scope. LOCKED terms
 * are NEVER updated (the backend 409s `TERM_LOCKED`) regardless of scope; a finished term that is
 * not yet locked IS updated. A term whose dates aren't found is
 * conservatively kept (matches the pre-scope behaviour). `viewedTermId` is the term the admin is
 * editing from (editingLesson.termId).
 *   - 'this'          → only the viewed term (or, if the viewed term is locked, the earliest
 *                       unlocked candidate — so the choice is never a silent no-op).
 *   - 'thisAndFuture' → the anchor term + any term starting on/after it (never locked).
 *   - 'all'           → every UNLOCKED term in the set.
 *
 * ANCHOR: normally the viewed term. But a grouped class's `viewedTermId` is only the API-ordering
 * seed and CAN be a locked term; if so (or if it's missing/not a candidate), we anchor to the
 * EARLIEST UNLOCKED candidate term. Without this, 'this' would filter to [] (silent no-op) and
 * 'thisAndFuture' would compare against a past startDate and behave like 'all'. Self-defending so
 * the helper is correct on its own, not only behind the read-only-edit guard.
 */
export function filterTermsByScope(
  candidateTermIds: string[],
  scope: EditScope,
  viewedTermId: string,
  terms: { id: string; startDate: string; endDate: string }[],
  isLocked: TermLockedPredicate
): string[] {
  const start = (id: string) => {
    const t = terms.find((x) => x.id === id);
    return t ? new Date(t.startDate).getTime() : 0;
  };

  // Unlocked candidates, earliest first — the anchor pool for 'this'/'thisAndFuture'.
  const liveCandidates = candidateTermIds
    .filter((id) => {
      const t = terms.find((x) => x.id === id);
      return !t /* unknown → keep */ || !isLocked(id);
    })
    .sort((a, b) => start(a) - start(b));

  const viewed = terms.find((t) => t.id === viewedTermId);
  // Anchor = the viewed term if it's a live candidate; else the earliest live candidate.
  const anchorId =
    viewed && !isLocked(viewed.id) && candidateTermIds.includes(viewedTermId)
      ? viewedTermId
      : liveCandidates[0];
  const anchorStart = anchorId ? start(anchorId) : 0;

  return candidateTermIds.filter((id) => {
    const term = terms.find((t) => t.id === id);
    if (!term) return true; // unknown dates → conservatively keep
    if (isLocked(id)) return false; // never rewrite a locked term
    if (scope === 'all') return true;
    if (scope === 'this') return id === anchorId;
    // thisAndFuture: the anchor term + any later term (by start date). Never past.
    return start(id) >= anchorStart;
  });
}

/**
 * Which existing term ids should be REMOVED (hard-deleted) for an edit, given the admin's new term
 * selection. A term is removed only when it was deselected AND is NOT locked — a locked term's
 * lesson is read-only history and must never be deleted through a membership edit (a whole-class
 * delete is a separate explicit action). So deselecting a locked term is a silent no-op.
 */
export function termsToRemoveForEdit(
  existingTermIds: string[],
  newTermIds: string[],
  terms: { id: string; startDate: string; endDate: string }[],
  isLocked: TermLockedPredicate
): string[] {
  return existingTermIds.filter((id) => {
    if (newTermIds.includes(id)) return false; // still selected → keep
    const t = terms.find((x) => x.id === id);
    if (t && isLocked(id)) return false; // deselected but locked → never delete history
    return true;
  });
}

// ===========================================
// PARTNER ELIGIBILITY (shared by the partner picker + the offer-to-partner modal)
// ===========================================

/** The `self` side of a candidate check — the lesson being created/edited. `id` is absent on create. */
export interface PartnerSelf {
  id?: string;
  teacherId: string;
  termId: string;
  dayOfWeek: number | null;
}

/** The minimal candidate shape (a `Lesson` satisfies this). */
export interface PartnerCandidateLesson {
  id: string;
  teacherId: string;
  termId: string;
  dayOfWeek: number | null;
  isActive: boolean;
  partnerLessonId: string | null;
  LessonType?: { type: string; allowPartnerOverlap: boolean } | null;
}

/**
 * Whether `candidate` is an eligible partner for the lesson being created/edited (`self`). Mirrors
 * the backend `validatePartnerLesson` rules so the FE picker AND the offer-to-partner modal never
 * diverge from what the server will accept:
 *   - not the lesson itself,
 *   - same teacher / term / day,
 *   - candidate is an active INDIVIDUAL type with allowPartnerOverlap on,
 *   - candidate is free (unpaired) OR already paired back to `self`.
 * The backend re-validates on save (`validatePartnerLesson`) — this is the FE convenience gate.
 */
export function isPartnerEligible(candidate: PartnerCandidateLesson, self: PartnerSelf): boolean {
  return (
    candidate.id !== self.id &&
    candidate.teacherId === self.teacherId &&
    candidate.termId === self.termId &&
    candidate.dayOfWeek === self.dayOfWeek &&
    candidate.LessonType?.type === 'INDIVIDUAL' &&
    !!candidate.LessonType?.allowPartnerOverlap &&
    candidate.isActive &&
    (!candidate.partnerLessonId || candidate.partnerLessonId === self.id)
  );
}

/**
 * Groups lessons by their core properties (name, teacher, room, day, time, isActive) so ONE
 * class that runs across several terms is ONE row, with a `termLessons` entry per term.
 * Lessons with different isActive status are grouped separately.
 *
 * ⚠️ When two lessons with the same properties sit in the SAME term, that key is NOT folded
 * at all: every lesson gets its own single-term row (#193). Two same-term lessons are two real
 * classes, and there is no way to tell which of the key's OTHER-term lessons belongs to which
 * of them — pairing them by position (a first cut of this fix) put the duplicate's own Term 4
 * lesson in the original's row, so a term edit or a whole-row delete there acted on the wrong
 * class. One row per lesson is always correct; the key folds again once the duplicate is gone.
 * Folding them (the behaviour until 2026-09-24) hid the second class from the list entirely,
 * and every edit path — `termLessons.find(tl => tl.termId === termId)` — silently acted on the
 * first one only. Measured in production: two Term 3 "Tue 5:00PM Voice: Foundation 1" classes,
 * the same student in both, one row shown.
 *
 * A same-term row is FLAGGED (`duplicateTermNames`) only when it is a likely mistake the admin
 * can still fix: a scheduled class (two "Schedule TBD" classes clash on nothing), not a legal
 * partner pair (`partnerLessonId` — the one sanctioned same-slot overlap), in a term that is not
 * LOCKED (a locked term is read-only, so a flag there could never be cleared; a term that has merely
 * finished is still editable until the next one starts).
 */
export function groupLessonsByProperties(
  lessons: Lesson[],
  isLocked: TermLockedPredicate = () => false
): GroupedLesson[] {
  // key → its lessons, in API order; Map iteration keeps each key's first-appearance order.
  const lessonsByKey = new Map<string, Lesson[]>();
  for (const lesson of lessons) {
    // Create a key based on properties that define a "same" lesson
    // Include isActive so active and inactive versions are shown separately
    const key = `${lesson.name}-${lesson.teacherId}-${lesson.roomId}-${lesson.dayOfWeek}-${lesson.startTime}-${lesson.endTime}-${lesson.isActive}`;
    const members = lessonsByKey.get(key);
    if (members) members.push(lesson);
    else lessonsByKey.set(key, [lesson]);
  }

  const termLessonOf = (lesson: Lesson) => ({
    id: lesson.id,
    termId: lesson.termId,
    termName: lesson.Term.name,
    startWeek: lesson.startWeek ?? null,
    endWeek: lesson.endWeek ?? null,
  });
  const rows: GroupedLesson[] = [];

  // Same term, so the same week count. Two classes clash only if they share a week they BOTH
  // run: the window, then a plain class's chosen weeks (class weeks — two fortnightly classes
  // alternating in one slot are not duplicates; /qa whole feature B-M1). A HYBRID reads as its
  // window (its pattern is not compared here). No term dates: assume they meet.
  const sessionsIntersect = (a: Lesson, b: Lesson): boolean => {
    if (!a.Term?.startDate || !a.Term?.endDate) return true;
    const termWeeks = termWeekCount(a.Term.startDate, a.Term.endDate);
    const weeksOf = (l: Lesson) =>
      classSessionWeeks({ ...l, LessonType: l.LessonType ?? null, LessonTermWeeks: l.LessonTermWeeks ?? null }, termWeeks) ??
      lessonWindowWeeks(l, termWeeks);
    const wb = new Set(weeksOf(b));
    return weeksOf(a).some((w) => wb.has(w));
  };

  for (const members of lessonsByKey.values()) {
    const termIds = members.map((l) => l.termId);
    const hasSameTermClash = new Set(termIds).size < termIds.length;
    if (!hasSameTermClash) {
      rows.push({ ...members[0], termLessons: members.map(termLessonOf) });
      continue;
    }
    for (const lesson of members) {
      const row: GroupedLesson = { ...lesson, termLessons: [termLessonOf(lesson)] };
      const isLikelyDuplicate = members.some(
        (other) =>
          other !== lesson &&
          other.termId === lesson.termId &&
          // A split class (weeks 1-4, then weeks 5-10, same slot) is two halves of ONE
          // class, not a mistake; so are two fortnightly classes on alternate weeks. They only
          // clash if they share a week they both run.
          sessionsIntersect(lesson, other) &&
          lesson.hasSchedule &&
          other.hasSchedule &&
          lesson.partnerLessonId !== other.id &&
          other.partnerLessonId !== lesson.id
      );
      if (isLikelyDuplicate && !isLocked(lesson.Term.id)) row.duplicateTermNames = [lesson.Term.name];
      rows.push(row);
    }
  }

  return rows;
}

/** The band-identity fields — MUST match the backend resolver's sibling-lookup fields
 * (resolveBandLessonIdForTerm in lesson.service.ts: name/teacherId/roomId/dayOfWeek/
 * startTime/endTime). If these ever diverge, the "Band: TBD" coverage preview would
 * disagree with what the backend actually links per term. */
export interface BandIdentityRow {
  id: string;
  termId: string;
  name: string;
  teacherId: string;
  roomId: string;
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
}

export interface BandCoverage {
  /** Selected termIds that contain a same-identity band (backend will link it). */
  present: string[];
  /** Selected termIds with NO matching band (backend leaves them "Band: TBD"). */
  missing: string[];
}

/**
 * For a hybrid class spanning `selectedTermIds`, work out which of those terms the chosen
 * band (`selectedBandId`) covers by identity — i.e. which terms have a same-identity band
 * row, and which will be saved as "Band: TBD". `allBands` is every active BAND lesson for
 * the school (one row per term). Returns null when there's nothing to signal (single term,
 * no band chosen, or the chosen band isn't in the list).
 *
 * The identity key is intentionally identical to the backend `resolveBandLessonIdForTerm`
 * so the preview never disagrees with the actual per-term linkage.
 */
export function computeBandCoverage(
  allBands: BandIdentityRow[],
  selectedBandId: string,
  selectedTermIds: string[]
): BandCoverage | null {
  if (selectedTermIds.length <= 1 || !selectedBandId || allBands.length === 0) return null;
  const selected = allBands.find((b) => b.id === selectedBandId);
  if (!selected) return null;

  const identity = (b: BandIdentityRow) =>
    `${b.name}|${b.teacherId}|${b.roomId}|${b.dayOfWeek}|${b.startTime}|${b.endTime}`;
  const selectedIdentity = identity(selected);
  const coveredTermIds = new Set(
    allBands.filter((b) => identity(b) === selectedIdentity).map((b) => b.termId)
  );

  return {
    present: selectedTermIds.filter((t) => coveredTermIds.has(t)),
    missing: selectedTermIds.filter((t) => !coveredTermIds.has(t)),
  };
}

// ===========================================
// HYBRID PATTERN — did the admin change it? (#174)
// ===========================================

/** The stored pattern fields an edit can change. Structural, so any API shape fits. */
export interface StoredHybridPattern {
  patternType?: string | null;
  hybridCombination?: string | null;
  groupWeeks?: unknown;
  individualWeeks?: unknown;
  bandLessonId?: string | null;
  individualSlotDuration?: number | null;
  bookingDeadlineHours?: number | null;
}

/** Only the fields the dialog can edit, so an unrelated formData change never counts. */
export interface HybridPatternFormFields {
  patternType: string;
  hybridCombination: string;
  groupWeeks: number[];
  individualWeeks: number[];
  bandLessonId: string;
  individualSlotDuration: number;
  bookingDeadlineHours: number;
}

const sortedNumbers = (v: unknown): number[] =>
  Array.isArray(v) ? [...(v as number[])].filter((w) => typeof w === 'number').sort((a, b) => a - b) : [];
/**
 * The fortnightly quick-fill (class weeks D3): `start, start+2, …` up to `last`, inclusive.
 * Empty when `start` is past `last`. A list, never a cadence: nothing about "fortnightly" is stored.
 */
export function fortnightlyWeeks(start: number, last: number): number[] {
  const weeks: number[] = [];
  for (let w = Math.max(1, Math.floor(start)); w <= last; w += 2) weeks.push(w);
  return weeks;
}

/** Order-insensitive week-list equality. Exported for `ClassWeeksConfig`'s refill guard (#177). */
export const sameNumbers = (a: unknown, b: unknown): boolean => {
  const x = sortedNumbers(a);
  const y = sortedNumbers(b);
  return x.length === y.length && x.every((w, i) => w === y[i]);
};

/**
 * `true` when the pattern the dialog would send is the one the seeding row
 * already has. The page uses it to OMIT `hybridPattern` from sibling-term updates
 * on a non-pattern edit (room, time, name…), so each sibling keeps its own weeks.
 *
 * Whole-feature /qa H1 (#174): the page used to resend ONE row's pattern to
 * every term on every save. With the per-term clamp that was a ratchet — an
 * edit seeded from a 9-week sibling rewrote the 11-week sibling to 9 sessions,
 * silently under-billing and un-scheduling it. Week order is irrelevant; a
 * combination's unused bucket is compared as the page sends it (empty); `''`
 * and `null` are the same "no band". No stored pattern (a type change TO
 * hybrid) is always a change.
 */
export function hybridPatternUnchanged(
  form: HybridPatternFormFields,
  stored: StoredHybridPattern | null | undefined
): boolean {
  if (!stored) return false;
  const combo = form.hybridCombination;
  const sentGroup = combo === 'INDIVIDUAL_BAND' ? [] : form.groupWeeks;
  const sentIndividual = combo === 'GROUP_BAND' ? [] : form.individualWeeks;
  const sentBand = combo !== 'GROUP_INDIVIDUAL' ? form.bandLessonId || null : null;
  return (
    form.patternType === (stored.patternType ?? '') &&
    combo === (stored.hybridCombination ?? 'GROUP_INDIVIDUAL') &&
    sameNumbers(sentGroup, stored.groupWeeks) &&
    sameNumbers(sentIndividual, stored.individualWeeks) &&
    sentBand === (stored.bandLessonId || null) &&
    form.individualSlotDuration === (stored.individualSlotDuration ?? 30) &&
    form.bookingDeadlineHours === (stored.bookingDeadlineHours ?? 24)
  );
}

// ===========================================
// CLASS WEEKS — the seeding row and "did the admin change it?" (#173, the band #174; class weeks Phase 7)
// ===========================================

/** A stored class-weeks row, as a lesson carries it (`LessonTermWeeks`, or the legacy `BandTerm`). Structural. */
export interface StoredClassWeeks {
  termId?: string;
  patternType?: string | null;
  /** `LessonTermWeeks.weeks`; the legacy `BandTerm` row calls it `rehearsalWeeks`. */
  weeks?: unknown;
  rehearsalWeeks?: unknown;
}

/** The row in one shape: `weeks` always set (from `weeks`, else the legacy `rehearsalWeeks`). */
export interface OwnClassWeeks {
  termId?: string;
  patternType: string | null;
  weeks: number[];
}

/**
 * The class-weeks row that belongs to THIS row's term (INDIVIDUAL / GROUP / BAND), read
 * from `LessonTermWeeks` and falling back to the legacy `BandTerm` copy (the server sends
 * both for one release, D11). Null = no row: an INDIVIDUAL / GROUP class runs every week.
 *
 * ⚠️ This does NOT fix the sibling ratchet. A multi-term class is N sibling rows each with
 * its own row, and `groupLessonsByProperties` spreads ONE — so `[0]` and this function return
 * the same object in the normal case (production 23/09/2026: 21 rows across 21 lessons, none
 * with more than one). **The ratchet is prevented by `classWeeksUnchanged`, not by this** — do
 * not read this as belt-and-braces and remove that gate.
 *
 * It is still worth having, because a lesson CAN accumulate rows for other terms:
 *   - moving a band lesson between terms upserts at the NEW `(lessonId, termId)` and
 *     never deletes the old row (`lesson.service.ts` updateLesson) — issue #175;
 *   - `upsertBandTerm` (`PUT /lessons/:id/band-term`) takes an arbitrary school-owned
 *     `termId` from the request body, which need not be the lesson's own.
 * In both cases `[0]` is a coin flip and this `find` picks the right row. The `[0]`
 * fallback is therefore reachable from a supported admin action, NOT just a test
 * double — when it fires the dialog seeds from a term the class has left.
 */
export function ownClassWeeks(lesson: {
  termId?: string;
  LessonTermWeeks?: StoredClassWeeks[] | null;
  BandTerm?: StoredClassWeeks[] | null;
}): OwnClassWeeks | null {
  const rows = lesson.LessonTermWeeks ?? lesson.BandTerm ?? [];
  const row = rows.find((r) => r.termId === lesson.termId) ?? rows[0] ?? null;
  if (!row) return null;
  const raw = row.weeks ?? row.rehearsalWeeks;
  return {
    termId: row.termId,
    patternType: row.patternType ?? null,
    weeks: Array.isArray(raw) ? (raw as unknown[]).filter((w): w is number => typeof w === 'number') : [],
  };
}

/**
 * `true` when the class weeks the dialog would send are the ones the seeding row already
 * has. The page uses it to OMIT `classWeeks` from sibling-term updates on any other edit, so
 * each sibling keeps its own weeks (#173 — a per-term clamp on the server without this gate
 * would be #174's ratchet).
 *
 * - **INDIVIDUAL / GROUP** (D2: EVERY_WEEK writes NO row): "Every week" is unchanged when no
 *   CUSTOM row is stored; a CUSTOM list is unchanged when it equals the stored CUSTOM list.
 * - **BAND**: no stored row is always a change, and EVERY_WEEK compares its WEEKS too —
 *   derived ONCE, `updateTerm` never re-derives them, so shortening a term strands a week
 *   beyond the new bound. Treating EVERY_WEEK as unconditionally "unchanged" sealed the only
 *   self-heal route: `bandTerm` was omitted from every term in the update loop, the
 *   server-side re-derive never fired, and the band card's "re-save this class to recalculate
 *   them" was advice the app could not honour. The #174 ratchet stays closed either way — an
 *   AGREEING list is still unchanged, so an ordinary room edit leaves the siblings alone.
 */
export function classWeeksUnchanged(
  form: { classWeeksPatternType: string; classWeeks: number[] },
  stored: OwnClassWeeks | null | undefined,
  lessonType: string | undefined
): boolean {
  if (lessonType !== 'BAND') {
    const storedCustom = stored?.patternType === 'CUSTOM';
    if (form.classWeeksPatternType === 'EVERY_WEEK') return !storedCustom;
    return storedCustom && sameNumbers(form.classWeeks, stored!.weeks);
  }
  if (!stored) return false;
  if (form.classWeeksPatternType !== (stored.patternType ?? '')) return false;
  return sameNumbers(form.classWeeks, stored.weeks);
}

// ===========================================
// Week options across the selected terms (#168 E1, shared with bands by #173 P3)
// ===========================================
// A class can span terms of DIFFERENT lengths. The picker offers weeks up to the
// LONGEST selected term and CAPTIONS the ones a shorter term lacks — never disables
// them, because week 11 may be right for Term 2 even though Term 4 will drop it. The
// server clamps per term and says so on save (#168 D), so nothing here has to guess.
//
// Extracted from `HybridPatternConfig` when the band picker needed the identical rule
// (#173 Phase 3). Two copies of this would drift, and the drift would be invisible:
// both render a plausible list either way.

export interface SelectedTermWeeks {
  id: string;
  name: string;
  weeks: number;
}

export interface TermWeekOptions {
  /** The selected terms that have a known week count, in selection order. */
  selectedTermWeeks: SelectedTermWeeks[];
  /** Weeks to offer: 1..longest selected term, or the transport ceiling when unknown. */
  weekOptions: number[];
  /** True when the selected terms are not all the same length — drives the info Alert. */
  termLengthsDiffer: boolean;
  /**
   * `Week 11 — not in Term 4 2026`, or plain `Week 11` when every term has it. When the class has
   * a window in its anchor term, a week outside it adds `not scheduled in Term 3 2026 (class runs
   * weeks 5–8)` — captioned, never disabled: the week still applies to the other terms.
   */
  weekLabel: (week: number) => string;
  /** The names of the selected terms that lack `week`. */
  termsMissingWeek: (week: number) => string[];
}

/**
 * @param termIds    every term the class spans (empty → falls back to `termId`)
 * @param termId     the primary/viewed term
 * @param termNames  id → display name
 * @param termWeeks  id → Monday-anchored `termWeekCount`. ABSENT → the transport
 *                   ceiling (`allWeeks`), i.e. the pre-2026-09-23 behaviour. A selected
 *                   term missing from the map is ignored by both the bound and the
 *                   captions (fail-open): a partial map must not silently narrow the
 *                   picker below what another term legitimately offers.
 * @param anchorWindow  the class window (mid-term start / last week) in its ANCHOR term, or
 *                  undefined for a full-term class. The window belongs to one term while these
 *                  pickers are shared by all selected terms, so a week outside it is CAPTIONED
 *                  ("not scheduled in {term}"), never removed from the options.
 */
export function resolveTermWeekOptions(
  allWeeks: number[],
  termIds: string[],
  termId: string | undefined,
  termNames: Record<string, string> | undefined,
  termWeeks: Record<string, number> | undefined,
  anchorWindow?: { termName: string; first: number; last: number } | null
): TermWeekOptions {
  const ids = termIds.length > 0 ? termIds : termId ? [termId] : [];
  const selectedTermWeeks = ids
    .filter((id) => termWeeks?.[id] !== undefined)
    .map((id) => ({ id, name: termNames?.[id] ?? id, weeks: termWeeks![id] }));

  const weekOptions =
    selectedTermWeeks.length === 0
      ? allWeeks
      : allWeeks.slice(0, Math.max(...selectedTermWeeks.map((t) => t.weeks)));

  const termsMissingWeek = (week: number): string[] =>
    selectedTermWeeks.filter((t) => t.weeks < week).map((t) => t.name);

  const weekLabel = (week: number): string => {
    const missing = termsMissingWeek(week);
    const parts: string[] = [];
    if (missing.length > 0) parts.push(`not in ${missing.join(', ')}`);
    // A term that lacks the week outright is already named above; do not say it twice.
    // An empty / inverted window (first > last, e.g. mid-edit while the two selects disagree)
    // has no honest sentence - "class runs weeks 8-5" - so it captions nothing.
    if (
      anchorWindow &&
      anchorWindow.first <= anchorWindow.last &&
      (week < anchorWindow.first || week > anchorWindow.last) &&
      !missing.includes(anchorWindow.termName)
    ) {
      const runs =
        anchorWindow.first === anchorWindow.last
          ? `week ${anchorWindow.first} only`
          : `weeks ${anchorWindow.first}–${anchorWindow.last}`;
      parts.push(`not scheduled in ${anchorWindow.termName} (class runs ${runs})`);
    }
    return parts.length === 0 ? `Week ${week}` : `Week ${week} — ${parts.join('; ')}`;
  };

  return {
    selectedTermWeeks,
    weekOptions,
    termLengthsDiffer:
      selectedTermWeeks.length > 1 && new Set(selectedTermWeeks.map((t) => t.weeks)).size > 1,
    weekLabel,
    termsMissingWeek,
  };
}

/** The shared copy for the "terms differ in length" Alert, longest first. */
export function termLengthsSentence(selectedTermWeeks: SelectedTermWeeks[]): string {
  return [...selectedTermWeeks]
    .sort((a, b) => b.weeks - a.weeks)
    .map((t) => `${t.name} has ${t.weeks} weeks`)
    .join(', ');
}

// ===========================================
// CLASS WINDOW — "Starts in week" / "Last week" (mid-term start, Phase 1)
// ===========================================
// The window lives on ONE term's row (the ANCHOR term). It is NOT part of `baseLessonData`
// and NOT in `groupLessonsByProperties`' key (two terms with different windows are still one
// class). The per-term update loop sends it to the anchor's row only, and only when the admin
// changed it — the same #174 rule as the hybrid pattern, or a room edit would push the anchor's
// window onto every sibling term.

export interface LessonWindowFields {
  startWeek: number | null;
  endWeek: number | null;
}

/** `1` is the default start (the server stores it as null); anything falsy is "not set". */
const normStart = (n: number | null | undefined): number | null => (n && n > 1 ? n : null);
const normEnd = (n: number | null | undefined): number | null => (n && n >= 1 ? n : null);

/**
 * The window stored on the ANCHOR term's own row. `GroupedLesson` spreads ONE arbitrary row,
 * so its own `startWeek` may belong to another term; `termLessons` carries every row's window.
 */
export function storedWindowOf(
  lesson: {
    termId: string;
    startWeek?: number | null;
    endWeek?: number | null;
    termLessons?: Array<{ termId: string; startWeek?: number | null; endWeek?: number | null }>;
  },
  anchorTermId?: string
): LessonWindowFields {
  const anchor = anchorTermId ?? lesson.termId;
  const row = lesson.termLessons?.find((tl) => tl.termId === anchor);
  const source = row ?? (anchor === lesson.termId ? lesson : null);
  return { startWeek: normStart(source?.startWeek), endWeek: normEnd(source?.endWeek) };
}

/** `true` when the dialog's window equals the one stored on the anchor term's row. */
export function windowUnchanged(
  form: LessonWindowFields,
  editingLesson: Parameters<typeof storedWindowOf>[0],
  anchorTermId?: string
): boolean {
  const stored = storedWindowOf(editingLesson, anchorTermId);
  return normStart(form.startWeek) === stored.startWeek && normEnd(form.endWeek) === stored.endWeek;
}

/** `true` when two windows are the same after normalising (week 1 = no start). */
export function sameWindow(a: LessonWindowFields, b: LessonWindowFields): boolean {
  return normStart(a.startWeek) === normStart(b.startWeek) && normEnd(a.endWeek) === normEnd(b.endWeek);
}

/**
 * The window fields for ONE term's update. Only the anchor term, and only when the window
 * changed; a cleared side is sent as `null` (the server: number sets, null clears, omitted
 * leaves alone). Everything else gets `{}` so the spread adds no key at all.
 */
export function windowForUpdate(
  termId: string,
  anchorTermId: string,
  form: LessonWindowFields,
  unchanged: boolean
): { startWeek?: number | null; endWeek?: number | null } {
  if (termId !== anchorTermId || unchanged) return {};
  return { startWeek: normStart(form.startWeek), endWeek: normEnd(form.endWeek) };
}

/** The window fields for a CREATE: only what is set (a full-term class sends neither). */
export function windowForCreate(form: LessonWindowFields): { startWeek?: number; endWeek?: number } {
  const startWeek = normStart(form.startWeek);
  const endWeek = normEnd(form.endWeek);
  return {
    ...(startWeek !== null ? { startWeek } : {}),
    ...(endWeek !== null ? { endWeek } : {}),
  };
}
